import { useState, useRef, useEffect, useCallback } from "react";
import { Mic, Trash2, Pause, Play, Send } from "lucide-react";
import { useReactMediaRecorder } from "react-media-recorder";
import toast from "react-hot-toast";
import Button from "shared/Button/Button";
import chatService from "../../services/chatService";
import { motion } from "framer-motion";

interface AudioRecorderProps {
  onTranscribed: (text: string) => void;
  onCancel: () => void;
  userId: string | null;
  sessionId: string | null;
}

const MIN_RECORDING_SECONDS = 5;
const MAX_RECORDING_SECONDS = 30;

const SENSITIVITY = 2.0;
const TARGET_FPS = 30;

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onTranscribed,
  onCancel,
  userId,
  sessionId,
}) => {
  const waveformHistory = useRef<number[]>([]);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const frameCounterRef = useRef<number>(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<any>(null);
  const animationRef = useRef<number | null>(null);
  const lastDrawTimeRef = useRef<number>(0);
  const recordingStartTimeRef = useRef<number | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingState, setRecordingState] = useState<
    "idle" | "recording" | "stopped" | "transcribing"
  >("idle");
  const recordingStateRef = useRef(recordingState);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // === NEW: refs for smooth progress ===
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const progressAnimationRef = useRef<number | null>(null);
  const isPlayingRef = useRef(false);

  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const isCancelledRef = useRef(false);

  const {
    startRecording: startMediaRecording,
    stopRecording: stopMediaRecording,
    clearBlobUrl,
  } = useReactMediaRecorder({
    audio: true,
    askPermissionOnMount: false,
    blobPropertyBag: { type: "audio/wav" },
    onStop: (blobUrl, blob) => {
      if (isCancelledRef.current) return;
      setAudioBlob(blob);
      setAudioUrl(blobUrl);
      setRecordingState("stopped");
      if (durationIntervalRef.current)
        clearInterval(durationIntervalRef.current);
    },
  });

  const resizeCanvasForDisplay = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const neededWidth = Math.round(rect.width * dpr);
    const neededHeight = Math.round(rect.height * dpr);
    if (canvas.width !== neededWidth || canvas.height !== neededHeight) {
      canvas.width = neededWidth;
      canvas.height = neededHeight;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }, []);

  const setupAudioVisualizer = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    const audioCtx = new AudioCtx();
    audioContextRef.current = audioCtx;

    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.6;
    analyserRef.current = analyser;

    const bufferLength = analyser.fftSize;
    dataArrayRef.current = new Float32Array(bufferLength);

    const source = audioCtx.createMediaStreamSource(stream);
    const gainNode = audioCtx.createGain();
    gainNode.gain.value = SENSITIVITY;
    gainNodeRef.current = gainNode;

    source.connect(gainNode);
    gainNode.connect(analyser);

    resizeCanvasForDisplay();
    window.addEventListener("resize", resizeCanvasForDisplay);
  }, [resizeCanvasForDisplay]);

  const drawVisualizer = useCallback((timestamp: number) => {
    const minDelta = 1000 / TARGET_FPS;
    if (!lastDrawTimeRef.current) lastDrawTimeRef.current = timestamp;
    const delta = timestamp - lastDrawTimeRef.current;
    if (delta < minDelta) {
      animationRef.current = requestAnimationFrame(drawVisualizer);
      return;
    }
    lastDrawTimeRef.current = timestamp;

    const analyser = analyserRef.current;
    const data = dataArrayRef.current;
    const canvas = canvasRef.current;
    if (!analyser || !data || !canvas) {
      animationRef.current = requestAnimationFrame(drawVisualizer);
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      animationRef.current = requestAnimationFrame(drawVisualizer);
      return;
    }

    analyser.getFloatTimeDomainData(data);

    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
    const rms = Math.sqrt(sum / data.length);
    const scaled = Math.min(1, rms * 6);

    waveformHistory.current.push(scaled);
    const cssWidth = canvas.width / (window.devicePixelRatio || 1);
    const barWidth = 3;
    const barSpacing = 1;
    const maxBars = Math.max(8, Math.floor(cssWidth / (barWidth + barSpacing)));
    if (waveformHistory.current.length > maxBars)
      waveformHistory.current.shift();

    const cssHeight = canvas.height / (window.devicePixelRatio || 1);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    ctx.beginPath();
    ctx.moveTo(0, cssHeight / 2);
    ctx.lineTo(cssWidth, cssHeight / 2);
    ctx.strokeStyle = "#e6e6e6";
    ctx.lineWidth = 1;
    ctx.stroke();

    for (let i = 0; i < waveformHistory.current.length; i++) {
      const amp =
        waveformHistory.current[waveformHistory.current.length - 1 - i];
      const barH = Math.max(2, amp * cssHeight * 1.1);
      const x = cssWidth - (i + 1) * (barWidth + barSpacing);
      const y = (cssHeight - barH) / 2;
      const ageRatio = i / Math.max(waveformHistory.current.length - 1, 1);
      const opacity = Math.max(0.35, 1 - ageRatio * 0.75);
      ctx.fillStyle = `rgba(59,130,246, ${opacity})`;
      ctx.fillRect(x, y, barWidth, barH);
    }

    if (recordingStateRef.current === "recording") {
      animationRef.current = requestAnimationFrame(drawVisualizer);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  useEffect(() => {
    recordingStateRef.current = recordingState;
  }, [recordingState]);

  const handleStartRecording = async () => {
    try {
      await setupAudioVisualizer();
      startMediaRecording();
      setRecordingState("recording");
      setRecordingDuration(0);
      waveformHistory.current = [];
      frameCounterRef.current = 0;
      recordingStartTimeRef.current = Date.now();

      durationIntervalRef.current = setInterval(() => {
        if (!recordingStartTimeRef.current) return;
        const elapsed = Math.floor(
          (Date.now() - recordingStartTimeRef.current) / 1000
        );
        setRecordingDuration(elapsed);
        if (elapsed >= MAX_RECORDING_SECONDS) handleStopRecording();
      }, 100);

      if (!animationRef.current)
        animationRef.current = requestAnimationFrame(drawVisualizer);
    } catch {
      toast.error("Could not access microphone");
      cleanupAudio();
      onCancel();
    }
  };

  const handleStopRecording = () => {
    if (recordingStateRef.current !== "recording") return;
    const start = recordingStartTimeRef.current;
    const elapsed = start ? Math.floor((Date.now() - start) / 1000) : 0;
    if (elapsed < MIN_RECORDING_SECONDS) {
      toast.error(
        `Recording must be at least ${MIN_RECORDING_SECONDS} seconds.`
      );
      return;
    }
    stopMediaRecording();
    setRecordingState("stopped");
    cleanupAudio();
  };

  const handleCancelRecording = () => {
    isCancelledRef.current = true;
    if (recordingState === "recording") stopMediaRecording();
    setRecordingState("idle");
    setIsTranscribing(false);
    setRecordingDuration(0);
    setCurrentTime(0);
    setAudioDuration(0);
    setIsPlaying(false);
    clearBlobUrl();
    setAudioBlob(null);
    setAudioUrl(null);
    waveformHistory.current = [];
    cleanupAudio();
    onCancel();
  };

  // === NEW: Smooth progress loop ===
  const animateProgress = useCallback(() => {
    if (!isPlayingRef.current || !audioRef.current || !progressBarRef.current) {
      return;
    }
    const dur = audioRef.current.duration || 0;
    const t = audioRef.current.currentTime || 0;
    const frac = dur > 0 ? Math.min(1, t / dur) : 0;
    progressBarRef.current.style.transform = `scaleX(${frac})`;
    progressAnimationRef.current = requestAnimationFrame(animateProgress);
  }, []);

  const handlePlayRecording = () => {
    if (!audioRef.current || !audioUrl) return;
    if (audioRef.current.currentTime >= audioRef.current.duration) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      if (progressBarRef.current)
        progressBarRef.current.style.transform = "scaleX(0)";
    }
    audioRef.current
      .play()
      .then(() => {
        setIsPlaying(true);
        isPlayingRef.current = true;
        if (!progressAnimationRef.current)
          progressAnimationRef.current = requestAnimationFrame(animateProgress);
      })
      .catch((err) => console.warn("Play failed:", err));
  };

  const handlePauseRecording = () => {
    audioRef.current?.pause();
    setIsPlaying(false);
    isPlayingRef.current = false;
    if (progressAnimationRef.current) {
      cancelAnimationFrame(progressAnimationRef.current);
      progressAnimationRef.current = null;
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || audioDuration <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percentage = (e.clientX - rect.left) / rect.width;
    const newTime = percentage * audioDuration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    if (progressBarRef.current)
      progressBarRef.current.style.transform = `scaleX(${percentage})`;
  };

  const handleTranscribeAndSend = async () => {
    const lengthSec = audioRef.current?.duration
      ? Math.floor(audioRef.current.duration)
      : recordingDuration;
    if (lengthSec < MIN_RECORDING_SECONDS) {
      toast.error(
        `Recording must be at least ${MIN_RECORDING_SECONDS} seconds.`
      );
      return;
    }
    if (!audioBlob || !userId || !sessionId) {
      toast.error("Missing audio or user/session info");
      handleCancelRecording();
      return;
    }
    setRecordingState("transcribing");
    setIsTranscribing(true);
    try {
      const transcribedText = await chatService.uploadAudio(
        audioBlob,
        sessionId,
        userId
      );
      if (transcribedText.transcription.trim())
        onTranscribed(transcribedText.transcription);
      else {
        toast.error("No speech detected");
        onCancel();
      }
    } catch (error: any) {
      toast.error(error.message || "Transcription failed");
      resetAfterTranscribe();
    } finally {
      resetAfterTranscribe();
    }
  };

  const resetAfterTranscribe = () => {
    setRecordingState("idle");
    setIsTranscribing(false);
    setAudioBlob(null);
    setAudioUrl(null);
    clearBlobUrl();
    waveformHistory.current = [];
    cleanupAudio();
  };

  const cleanupAudio = useCallback(() => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    if (progressAnimationRef.current)
      cancelAnimationFrame(progressAnimationRef.current);
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    try {
      gainNodeRef.current?.disconnect();
      analyserRef.current?.disconnect();
      gainNodeRef.current = null;
      analyserRef.current = null;
      if (audioContextRef.current?.state !== "closed")
        audioContextRef.current?.close();
      audioContextRef.current = null;
    } catch (e) {
      console.warn("cleanupAudio error:", e);
    }

    window.removeEventListener("resize", resizeCanvasForDisplay);
    if (progressBarRef.current)
      progressBarRef.current.style.transform = "scaleX(0)";
  }, [resizeCanvasForDisplay]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    handleStartRecording();
    return () => {
      cleanupAudio();
    };
  }, []);

  return (
    <div className="flex flex-col sm:flex-row items-center bg-gray-50 rounded-lg sm:space-x-3 space-y-3 sm:space-y-0">
      {recordingState === "recording" && (
        <div className="flex-1 flex items-center space-x-3 w-full mt-2">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <div className="flex-1 h-10 bg-white rounded border">
            <canvas ref={canvasRef} className="w-full h-full rounded" />
          </div>
          <span className="text-sm font-mono text-gray-500 min-w-[40px]">
            {formatDuration(recordingDuration)}
          </span>
        </div>
      )}

      {recordingState === "stopped" && (
        <div className="flex-1 w-full flex flex-col sm:flex-row items-center sm:space-x-3 space-y-2 sm:space-y-0 mt-7">
          <audio
            ref={audioRef}
            src={audioUrl || undefined}
            onPlay={() => {
              setIsPlaying(true);
              isPlayingRef.current = true;
              if (!progressAnimationRef.current)
                progressAnimationRef.current =
                  requestAnimationFrame(animateProgress);
            }}
            onPause={() => {
              setIsPlaying(false);
              isPlayingRef.current = false;
              if (progressAnimationRef.current) {
                cancelAnimationFrame(progressAnimationRef.current);
                progressAnimationRef.current = null;
              }
            }}
            onLoadedMetadata={() => {
              if (audioRef.current) {
                setAudioDuration(audioRef.current.duration || 0);
              }
            }}
            onTimeUpdate={() => {
              if (audioRef.current)
                setCurrentTime(audioRef.current.currentTime);
            }}
            onEnded={() => {
              setIsPlaying(false);
              isPlayingRef.current = false;
              if (progressAnimationRef.current) {
                cancelAnimationFrame(progressAnimationRef.current);
                progressAnimationRef.current = null;
              }
              if (progressBarRef.current)
                progressBarRef.current.style.transform = "scaleX(1)";
            }}
          />

          <div className="flex-1 w-full flex flex-col space-y-2">
            <div
              className="w-full h-2 bg-gray-200 rounded-full cursor-pointer overflow-hidden"
              onClick={handleProgressClick}
            >
              <div
                ref={progressBarRef}
                className="h-full bg-btn-primary rounded-full"
                style={{
                  transform: "scaleX(0)",
                  transformOrigin: "0 50%",
                  willChange: "transform",
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>{formatDuration(Math.floor(currentTime))}</span>
              <span>{formatDuration(Math.floor(audioDuration))}</span>
            </div>
          </div>
        </div>
      )}

      {recordingState === "transcribing" && (
        <div className="w-full flex items-center justify-center gap-4 py-2">
          <motion.div
            className="flex space-x-1"
            animate={{
              transition: { staggerChildren: 0.2, repeat: Infinity },
            }}
          >
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                className="w-2 h-2 bg-blue-500 rounded-full"
                animate={{ scale: [1, 1.2, 1], opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </motion.div>
          <span className="text-sm text-gray-600">Transcribing...</span>
        </div>
      )}

      <div className="flex items-center space-x-2">
        {recordingState === "recording" && (
          <Button className="mt-2" onClick={handleStopRecording}>
            <Pause className="w-4 h-4 " />
          </Button>
        )}

        {recordingState === "stopped" && (
          <Button
            className="mt-2"
            onClick={isPlaying ? handlePauseRecording : handlePlayRecording}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 text-indigo-400" />
            )}
          </Button>
        )}

        {(recordingState === "recording" || recordingState === "stopped") && (
          <>
            <Button onClick={handleCancelRecording} className="mt-2">
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
            {recordingState === "stopped" && (
              <Button
                className="mt-2"
                onClick={handleTranscribeAndSend}
                disabled={isTranscribing}
              >
                <Send className="w-4 h-4 " />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AudioRecorder;
